// The cash-out screen (APP_STATE.CASHED_OUT; money-mode §4, §7): the round-results chrome with
// "Cashed out!", the value received in USD (large) and SOL, a TEST MONEY tag while the server
// runs the fake settlement, a "held for review" note after NOTICE payoutDelayed, the same six
// stats and mass graph as the death screen, and Continue (Esc continues too).
//
// It appears on the same schedule as the results card (mounted at +0.36 s, fade +2.95 → 3.45 s)
// so both end-of-life screens feel the same.
//
// Implements app.ts `CashOutScreen`. Consumer: app.ts (assigned in start()).

import type { CashOutDoneMessage, Rgb } from '@ballio/shared';
import type { CashOutScreen, CashOutView } from '../../app.js';
import { h, show } from '../dom.js';
import { t } from '../i18n/t.js';
import {
  continueReady,
  drawMassGraph,
  GRAPH_H,
  GRAPH_W,
  practiceResultsButtons,
  revealResults,
  statsBlock,
} from './summary.js';

export interface CashOutDeps {
  onContinue(): void;
  /** Practice rooms: "Play for real · $1" (leave practice for the money door). */
  onPlayForReal?(): void;
  playClick?(): void;
  /** The server runs the fake settlement (show TEST MONEY). */
  isTestMoney(): boolean;
}

export interface CashOutCard extends CashOutScreen {
  readonly el: HTMLElement;
}

/** The lines the cash-out card shows above the stats (DOM-free). */
export function cashOutLines(
  view: Pick<CashOutView, 'usdText' | 'solText' | 'delayed' | 'practice'>,
  testMoney: boolean,
): {
  title: string;
  caption: string;
  usd: string;
  sol: string;
  tag: string | null;
  note: string | null;
} {
  if (view.practice) {
    // money-mode §1.4: "Practice cash-out: $x (play money)", the PRACTICE tag, no SOL line.
    return {
      title: t('practice.cashout.title'),
      caption: t('practice.cashout.caption'),
      usd: view.usdText,
      sol: t('practice.cashout.playMoney'),
      tag: t('practice.badge'),
      note: null,
    };
  }
  return {
    title: t('money.cashout.title'),
    caption: t('money.cashout.received'),
    usd: view.usdText,
    sol: view.solText,
    tag: testMoney ? t('money.test') : null,
    note: view.delayed ? t('money.cashout.delayed') : null,
  };
}

export function createCashOutScreen(deps: CashOutDeps): CashOutCard {
  let visible = false;
  let mounted = false;
  let shownAt = Number.NEGATIVE_INFINITY;
  let cancelReveal: (() => void) | null = null;
  let lastView: CashOutView | null = null;

  const title = h('h2', { class: 'results-title', id: 'cashout-title' }, t('money.cashout.title'));
  const caption = h('p', { class: 'cashout-caption' });
  const usd = h('p', { class: 'cashout-usd' });
  const sol = h('p', { class: 'cashout-sol' });
  const tag = h('span', { class: 'money-tag', hidden: true });
  const note = h('p', { class: 'cashout-note', role: 'status', hidden: true });
  const statsWrap = h('div', { class: 'results-stats-wrap' });
  const graph = h('canvas', {
    class: 'results-graph',
    width: GRAPH_W,
    height: GRAPH_H,
    role: 'img',
    'aria-label': t('summary.graph'),
  });
  const continueBtn = h(
    'button',
    {
      type: 'button',
      class: 'btn-results',
      onClick: () => {
        if (!mounted) return;
        deps.playClick?.();
        deps.onContinue();
      },
    },
    t('summary.continue'),
  );
  const el = h(
    'section',
    { class: 'card results-card cashout-card', 'aria-labelledby': 'cashout-title', hidden: true },
    title,
    h(
      'div',
      { class: 'cashout-amount' },
      caption,
      usd,
      h('div', { class: 'cashout-sub' }, sol, tag),
    ),
    note,
    statsWrap,
    h('hr', { class: 'results-rule' }),
    graph,
    continueBtn,
  );
  const real = practiceResultsButtons(
    el,
    continueBtn,
    () => mounted,
    deps.onPlayForReal,
    deps.playClick,
  );
  el.append(real.el);

  const render = (): void => {
    if (!lastView) return;
    const lines = cashOutLines(lastView, deps.isTestMoney());
    title.textContent = lines.title;
    el.classList.toggle('is-practice', lastView.practice != null);
    real.apply(lastView.practice ?? null);
    caption.textContent = lines.caption;
    usd.textContent = lines.usd;
    sol.textContent = lines.sol;
    tag.textContent = lines.tag ?? '';
    show(tag, lines.tag !== null);
    note.textContent = lines.note ?? '';
    show(note, lines.note !== null);
    el.classList.toggle('has-note', lines.note !== null);
  };

  return {
    el,
    get visible() {
      return visible;
    },
    show(msg: CashOutDoneMessage, view: CashOutView, nowMs: number, color?: Rgb): void {
      lastView = view;
      render();
      statsWrap.replaceChildren(statsBlock(msg.stats));
      drawMassGraph(graph, msg.stats.samples, color ?? 0xffc400);
      visible = true;
      mounted = false;
      shownAt = nowMs;
      show(el, false);
      cancelReveal?.();
      cancelReveal = revealResults(el, () => {
        mounted = true;
        // ui-qol §7: Enter does nothing here (agar leaves focus on the page), so no button
        // keeps focus; Esc continues (app.ts).
        const active = el.ownerDocument.activeElement;
        if (active instanceof HTMLElement && active !== el.ownerDocument.body) active.blur();
      });
    },
    setDelayed(delayed: boolean): void {
      if (!lastView) return;
      lastView = { ...lastView, delayed };
      render();
    },
    hide(): void {
      visible = false;
      mounted = false;
      cancelReveal?.();
      cancelReveal = null;
      el.classList.remove('is-revealing');
      show(el, false);
    },
    tryContinue(nowMs: number): boolean {
      if (!visible || !(mounted || continueReady(shownAt, nowMs))) return false;
      deps.onContinue();
      return true;
    },
  };
}
